import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/shared";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Check, Search, RefreshCw } from "lucide-react";
import { getAccessLabel } from "./MisVisitas";

function AccessChip({ v }) {
    const { text, tone } = getAccessLabel(v);
    const cls =
        tone === "ok"
            ? "bg-success/15 text-success border-success/40"
            : tone === "warn"
                ? "bg-warning/15 text-warning border-warning/50"
                : "bg-muted text-muted-foreground border-border";
    return (
        <span
            data-testid={`recep-access-${v.visita_id}`}
            className={`inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded-full border whitespace-nowrap ${cls}`}
        >
            {text}
        </span>
    );
}

function fmtHora(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

const WEEKDAYS = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];

function fmtAutorizadoPara(yyyymmdd) {
    if (!yyyymmdd) return "—";
    const [y, m, d] = yyyymmdd.split("-").map(Number);
    const date = new Date(y, m - 1, d);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const diff = Math.round((date - today) / 86400000);
    if (diff === 0) return "Hoy";
    if (diff === 1) return "Mañana";
    if (diff === -1) return "Ayer";
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    return `${WEEKDAYS[date.getDay()]} ${dd}/${mm}`;
}

const FILTROS = [
    { value: "todos", label: "Todos" },
    { value: "no_llego", label: "No llegó aún" },
    { value: "llego", label: "Ya llegaron" },
];

export default function VisitasHoy({ showAll = false }) {
    const today = new Date().toISOString().slice(0, 10);
    const [items, setItems] = useState([]);
    const [unidades, setUnidades] = useState([]);
    const [fecha, setFecha] = useState(showAll ? "" : today);
    const [estado, setEstado] = useState("todos");
    const [query, setQuery] = useState("");
    const [busy, setBusy] = useState({});
    const [lastUpdate, setLastUpdate] = useState(null);
    const [refreshing, setRefreshing] = useState(false);

    // Refs para que el polling lea siempre los valores frescos sin reiniciar el intervalo
    const fechaRef = useRef(fecha);
    const estadoRef = useRef(estado);
    useEffect(() => { fechaRef.current = fecha; }, [fecha]);
    useEffect(() => { estadoRef.current = estado; }, [estado]);

    const load = async ({ silent = false } = {}) => {
        const params = {};
        if (fechaRef.current) params.fecha = fechaRef.current;
        if (estadoRef.current && estadoRef.current !== "todos") params.estado = estadoRef.current;
        if (!silent) setRefreshing(true);
        try {
            const r = await api.get("/visitas", { params });
            setItems(r.data);
            setLastUpdate(new Date());
        } catch (e) {
            if (!silent) toast.error(formatApiError(e));
        } finally {
            if (!silent) setRefreshing(false);
        }
    };

    useEffect(() => { api.get("/unidades").then((r) => setUnidades(r.data)); }, []);
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [fecha, estado]);

    // Auto-refresh: solo en pantalla recepción (no en showAll), cada 10s, pausa si tab está oculta
    useEffect(() => {
        if (showAll) return undefined;
        const id = setInterval(() => {
            if (typeof document !== "undefined" && document.hidden) return;
            load({ silent: true });
        }, 10_000);
        return () => clearInterval(id);
        // eslint-disable-next-line
    }, [showAll]);

    const uniName = (id) => unidades.find((u) => u.unidad_id === id)?.etiqueta || "—";

    const marcarLlegada = async (v) => {
        setBusy((b) => ({ ...b, [v.visita_id]: true }));
        // optimistic
        setItems((prev) => prev.map((x) => x.visita_id === v.visita_id ? { ...x, estado: "llego", hora_ingreso: new Date().toISOString() } : x));
        try {
            await api.post(`/visitas/${v.visita_id}/llegada`);
        } catch (e) {
            toast.error(formatApiError(e));
            load({ silent: true });
        } finally {
            setBusy((b) => { const n = { ...b }; delete n[v.visita_id]; return n; });
        }
    };

    const filtrados = useMemo(() => {
        const q = query.trim().toLowerCase();
        let arr = items;
        if (q) {
            arr = arr.filter((v) => {
                const uni = uniName(v.unidad_id).toLowerCase();
                return (
                    (v.nombre_visita || "").toLowerCase().includes(q) ||
                    (v.anfitrion_nombre || "").toLowerCase().includes(q) ||
                    uni.includes(q)
                );
            });
        }
        // Orden: no_llego primero por creado_en desc; luego llegados por hora_ingreso desc
        return [...arr].sort((a, b) => {
            if (a.estado !== b.estado) return a.estado === "no_llego" ? -1 : 1;
            if (a.estado === "no_llego") {
                return (b.creado_en || "").localeCompare(a.creado_en || "");
            }
            return (b.hora_ingreso || "").localeCompare(a.hora_ingreso || "");
        });
    }, [items, query, unidades]);

    const pendientes = filtrados.filter((v) => v.estado === "no_llego").length;
    const llegaron = filtrados.filter((v) => v.estado === "llego").length;

    const isToday = fecha === today;
    const titulo = showAll
        ? "Visitas (todas)"
        : isToday
            ? "Visitas de hoy"
            : `Visitas — ${fmtAutorizadoPara(fecha) || fecha}`;
    const subtitulo = showAll
        ? "Historial completo."
        : isToday
            ? today
            : fecha;

    return (
        <div>
            <PageHeader title={titulo} subtitle={subtitulo} />

            {/* Toolbar compacta */}
            <div className="flex flex-col md:flex-row gap-2 mb-3">
                <div className="relative flex-1">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    <Input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Buscar visitante, unidad o anfitrión…"
                        className="pl-8 h-9"
                        data-testid="visitas-search"
                        autoFocus
                    />
                </div>
                <div className="flex gap-2">
                    <Input
                        type="date"
                        value={fecha}
                        onChange={(e) => setFecha(e.target.value || today)}
                        className="h-9 w-40"
                        data-testid="visitas-fecha"
                        title="Cambiar fecha"
                    />
                    {!isToday && !showAll && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setFecha(today)}
                            className="h-9 px-3"
                            data-testid="visitas-fecha-hoy"
                            title="Volver a hoy"
                        >
                            Hoy
                        </Button>
                    )}
                    <div className="inline-flex rounded-md border border-border overflow-hidden h-9">
                        {FILTROS.map((f) => (
                            <button
                                key={f.value}
                                type="button"
                                onClick={() => setEstado(f.value)}
                                data-testid={`filtro-${f.value}`}
                                className={`px-3 text-xs font-medium transition-colors ${
                                    estado === f.value
                                        ? "bg-primary text-primary-foreground"
                                        : "bg-background text-muted-foreground hover:bg-primary-soft"
                                }`}
                            >
                                {f.label}
                            </button>
                        ))}
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => load()}
                        disabled={refreshing}
                        className="h-9 px-3"
                        data-testid="visitas-refresh"
                        title="Actualizar"
                    >
                        <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
                        Actualizar
                    </Button>
                </div>
            </div>

            {/* Indicador de auto-refresh */}
            {!showAll && (
                <p className="text-[11px] text-muted-foreground/70 mb-2 flex items-center gap-1.5" data-testid="visitas-autorefresh-hint">
                    <span className="inline-block h-1.5 w-1.5 rounded-full bg-success/60 animate-pulse" />
                    Actualización automática cada 10 s
                    {lastUpdate && (
                        <span className="font-mono">· última {String(lastUpdate.getHours()).padStart(2, "0")}:{String(lastUpdate.getMinutes()).padStart(2, "0")}:{String(lastUpdate.getSeconds()).padStart(2, "0")}</span>
                    )}
                </p>
            )}

            {/* Resumen */}
            {filtrados.length > 0 && (
                <div className="flex items-center gap-3 mb-2 text-[11px] text-muted-foreground font-mono">
                    <span>{filtrados.length} total</span>
                    {pendientes > 0 && (
                        <span className="text-accent">· {pendientes} pendiente{pendientes === 1 ? "" : "s"}</span>
                    )}
                    {llegaron > 0 && (
                        <span className="text-success">· {llegaron} llegaron</span>
                    )}
                </div>
            )}

            {filtrados.length === 0 ? (
                <EmptyState
                    title={query ? "Sin coincidencias" : "Sin visitas"}
                    description={query ? "Probá con otro nombre o unidad." : undefined}
                />
            ) : (
                <Card className="overflow-hidden">
                    {/* Tabla desktop */}
                    <div className="hidden md:block w-full">
                        <table className="w-full text-[13px]">
                            <thead className="bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
                                <tr>
                                    <th className="text-left font-semibold px-3 py-2 w-[110px]">Autorizado</th>
                                    <th className="text-left font-semibold px-3 py-2">Visitante</th>
                                    <th className="text-left font-semibold px-3 py-2 w-[110px]">Unidad</th>
                                    <th className="text-left font-semibold px-3 py-2 w-[200px]">Anfitrión</th>
                                    <th className="text-left font-semibold px-3 py-2 w-[140px]">Estado</th>
                                    <th className="px-3 py-2 w-[110px]"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtrados.map((v) => {
                                    const llego = v.estado === "llego";
                                    return (
                                        <tr
                                            key={v.visita_id}
                                            className={`border-t border-border hover:bg-primary-soft/30 ${llego ? "opacity-70" : ""}`}
                                            data-testid={`visita-row-${v.visita_id}`}
                                        >
                                            <td className="px-3 py-1.5 whitespace-nowrap text-xs">
                                                <span className={isToday ? "text-muted-foreground" : "text-primary font-mono"}>{fmtAutorizadoPara(v.fecha)}</span>
                                            </td>
                                            <td className="px-3 py-1.5 font-medium truncate">
                                                <div className="flex items-center gap-1.5 min-w-0">
                                                    <span className="truncate">{v.nombre_visita}</span>
                                                    <AccessChip v={v} />
                                                </div>
                                            </td>
                                            <td className="px-3 py-1.5 font-mono text-muted-foreground">{uniName(v.unidad_id)}</td>
                                            <td className="px-3 py-1.5 truncate">{v.anfitrion_nombre || "—"}</td>
                                            <td className="px-3 py-1.5">
                                                {llego ? (
                                                    <Badge className="bg-success/15 text-success border border-success/40 hover:bg-success/15 text-[10px] font-medium whitespace-nowrap">
                                                        Llegó <span className="font-mono ml-1">{fmtHora(v.hora_ingreso)}</span>
                                                    </Badge>
                                                ) : (
                                                    <Badge className="bg-accent/10 text-accent border border-accent/30 hover:bg-accent/10 text-[10px] font-medium">
                                                        No llegó aún
                                                    </Badge>
                                                )}
                                            </td>
                                            <td className="px-3 py-1.5 text-right">
                                                {!llego && !showAll && (
                                                    <Button
                                                        size="sm"
                                                        onClick={() => marcarLlegada(v)}
                                                        disabled={!!busy[v.visita_id]}
                                                        className="h-7 px-3 text-xs"
                                                        data-testid={`llegada-${v.visita_id}`}
                                                    >
                                                        <Check className="h-3.5 w-3.5 mr-1" /> Llegó
                                                    </Button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Lista mobile */}
                    <div className="md:hidden divide-y divide-border">
                        {filtrados.map((v) => {
                            const llego = v.estado === "llego";
                            return (
                                <div
                                    key={v.visita_id}
                                    className={`p-3 ${llego ? "opacity-70" : ""}`}
                                    data-testid={`visita-row-${v.visita_id}`}
                                >
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0 flex-1">
                                            <p className="font-medium text-sm truncate">{v.nombre_visita}</p>
                                            <p className="text-[11px] text-muted-foreground truncate">
                                                {uniName(v.unidad_id)} · {v.anfitrion_nombre || "—"}
                                            </p>
                                            <p className="text-[11px] text-muted-foreground">
                                                Autorizado para: <span className={isToday ? "" : "text-primary font-mono"}>{fmtAutorizadoPara(v.fecha)}</span>
                                            </p>
                                            <div className="mt-1">
                                                <AccessChip v={v} />
                                            </div>
                                        </div>
                                        {llego && (
                                            <div className="font-mono text-xs text-success shrink-0">
                                                Llegó {fmtHora(v.hora_ingreso)}
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex items-center justify-between gap-2 mt-2">
                                        {llego ? (
                                            <Badge className="bg-success/15 text-success border border-success/40 hover:bg-success/15 text-[10px] font-medium">
                                                Llegó
                                            </Badge>
                                        ) : (
                                            <Badge className="bg-accent/10 text-accent border border-accent/30 hover:bg-accent/10 text-[10px] font-medium">
                                                No llegó aún
                                            </Badge>
                                        )}
                                        {!llego && !showAll && (
                                            <Button
                                                size="sm"
                                                onClick={() => marcarLlegada(v)}
                                                disabled={!!busy[v.visita_id]}
                                                className="h-7 px-3 text-xs"
                                                data-testid={`llegada-${v.visita_id}`}
                                            >
                                                <Check className="h-3.5 w-3.5 mr-1" /> Llegó
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </Card>
            )}
        </div>
    );
}
